import React, { useEffect, useState } from 'react';
import { UploadCloud, FileText, X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ALLOWED = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'];

export default function FileUpload({
  kind = 'other',
  entityId, startupId, agreementId, opportunityId, minutesId,
  onUploaded,
  allowedKinds = null,
  label = 'Upload document',
  testid = 'file-upload',
}) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedKind, setSelectedKind] = useState(kind);

  const load = async () => {
    const params = {};
    if (entityId) params.entity_id = entityId;
    if (startupId) params.startup_id = startupId;
    if (agreementId) params.agreement_id = agreementId;
    if (opportunityId) params.opportunity_id = opportunityId;
    if (minutesId) params.minutes_id = minutesId;
    try {
      const { data } = await api.get('/documents/', { params });
      setFiles(data);
    } catch { /* */ }
  };
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
  useEffect(() => { load(); }, [entityId, startupId, agreementId, opportunityId, minutesId]);
  /* eslint-enable */

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error(`Unsupported: .${ext}. Allowed: ${ALLOWED.join(', ')}`);
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error('Max 50 MB');
      return;
    }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('kind', selectedKind);
    if (entityId) fd.append('entity_id', entityId);
    if (startupId) fd.append('startup_id', startupId);
    if (agreementId) fd.append('agreement_id', agreementId);
    if (opportunityId) fd.append('opportunity_id', opportunityId);
    if (minutesId) fd.append('minutes_id', minutesId);
    setBusy(true);
    try {
      const { data } = await api.post('/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${data.original_filename}`);
      load();
      onUploaded?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/documents/${doc.document_id}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Failed'); }
  };

  const downloadUrl = (doc) => {
    // Same-origin download — the HttpOnly session_token cookie rides
    // automatically, so the `?auth=` query-string fallback used by the
    // pre-c.2 localStorage flow is no longer required.
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/documents/${doc.document_id}/download`;
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="flex items-center gap-2">
          {allowedKinds && (
            <select value={selectedKind} onChange={(e) => setSelectedKind(e.target.value)} className="text-xs rounded border border-slate-200 bg-white px-2 py-1 focus:outline-none focus:border-[#8B3FC5]">
              {allowedKinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          )}
          <label className={`ve-btn-secondary text-xs px-3 py-1.5 cursor-pointer ${busy ? 'opacity-50' : ''}`} data-testid={`${testid}-input`}>
            {busy ? <Loader2 size={12} className="animate-spin"/> : <UploadCloud size={12}/>}
            <span>{busy ? 'Uploading…' : 'Upload'}</span>
            <input type="file" hidden onChange={upload} disabled={busy} accept={ALLOWED.map((e) => '.' + e).join(',')}/>
          </label>
        </div>
      </div>
      <div className="space-y-2">
        {files.length === 0 && (
          <div className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-md p-4 text-center">
            No documents yet.
          </div>
        )}
        {files.map((d) => (
          <div key={d.document_id} className="flex items-center justify-between gap-3 ve-card p-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={16} className="text-[#5E1F8B] shrink-0"/>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{d.original_filename}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {d.kind.replace(/_/g, ' ')} · {Math.round((d.size || 0) / 1024)} KB · {new Date(d.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={downloadUrl(d)} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-[#5E1F8B] hover:text-[#0A0A0A]" title="Open">
                <Download size={13}/> Open
              </a>
              <button onClick={() => remove(d)} className="text-xs text-slate-400 hover:text-rose-600" title="Remove">
                <X size={14}/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
